import React from 'react';
import { Link } from 'react-router-dom';
import { 
  TrendingUp, 
  Shield, 
  AlertTriangle, 
  CheckCircle2, 
  ArrowRight,
  Building2,
  DollarSign,
  Clock
} from 'lucide-react';

const PremiumFinancingPage = () => {
  const benefits = [
    {
      icon: DollarSign,
      title: "Preserve Liquidity",
      description: "Keep your capital working in your business or investments while still securing substantial coverage."
    },
    {
      icon: TrendingUp,
      title: "Leverage Opportunities",
      description: "Potentially achieve higher returns by deploying capital elsewhere while financing premiums."
    },
    {
      icon: Shield,
      title: "Maximize Coverage",
      description: "Acquire larger policies than might otherwise be affordable with annual premium payments."
    },
    {
      icon: Clock,
      title: "Flexible Structures",
      description: "Various financing arrangements tailored to your cash flow and financial goals."
    }
  ];

  const suitableFor = [
    "Business owners needing key person or buy-sell insurance",
    "Executives with substantial compensation and investment portfolios",
    "Real estate investors seeking estate liquidity",
    "Families planning significant wealth transfers",
    "High-income professionals with limited current liquidity"
  ];

  const considerations = [
    {
      title: "Interest Rate Risk",
      description: "Financing rates may fluctuate, affecting the overall cost of the strategy."
    },
    {
      title: "Policy Performance",
      description: "The strategy depends partly on policy cash value growth meeting projections."
    },
    {
      title: "Collateral Requirements",
      description: "Lenders typically require the policy and possibly additional collateral."
    },
    {
      title: "Annual Review",
      description: "These arrangements require regular monitoring and potential adjustments."
    }
  ];

  return (
    <div className="bg-[#020617] min-h-screen pt-20" data-testid="premium-financing-page">
      {/* Hero Section */}
      <section className="py-24 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1768557410710-01f506326bf7?w=1920)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#020617] via-transparent to-[#020617]" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="text-[#C5A059] text-sm uppercase tracking-widest mb-4 block">
              Advanced Strategies
            </span>
            <h1 className="font-serif text-4xl md:text-5xl text-white leading-tight mb-6">
              Premium Financing Solutions
            </h1>
            <p className="text-slate-300 text-xl mb-8 leading-relaxed">
              Sophisticated financing strategies that allow high-net-worth individuals to secure substantial life insurance coverage while preserving capital for other opportunities.
            </p>
            <div className="flex items-center space-x-2 text-slate-400 text-sm">
              <AlertTriangle className="w-4 h-4 text-[#C5A059]" />
              <span>Premium financing involves risks and is suitable only for qualified individuals</span>
            </div>
          </div>
        </div>
      </section>

      {/* What is Premium Financing */}
      <section className="py-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-serif text-3xl text-white mb-6">
                What is Premium Financing?
              </h2>
              <p className="text-slate-400 mb-6 leading-relaxed">
                Premium financing is a strategy where clients borrow funds from a third-party lender to pay life insurance premiums. Instead of using personal capital for large premium payments, clients can leverage financing to secure coverage while keeping their assets invested.
              </p>
              <p className="text-slate-400 mb-6 leading-relaxed">
                The life insurance policy's cash value and death benefit typically serve as collateral for the loan. Over time, the policy's cash value may grow sufficiently to repay the loan, depending on policy performance and market conditions.
              </p>
              <div className="p-6 bg-slate-900/50 border border-slate-800 rounded-lg">
                <p className="text-slate-300 text-sm italic">
                  "Premium financing is not for everyone. It requires careful analysis of your financial situation, risk tolerance, and long-term goals. Our specialists help you determine if this strategy aligns with your objectives."
                </p>
              </div>
            </div>

            <div className="bg-slate-900/50 border border-slate-800 p-8 rounded-lg">
              <h3 className="text-white font-semibold text-lg mb-6">How It Works</h3>
              <div className="space-y-6">
                {[
                  { step: "1", title: "Assessment", desc: "We analyze your financial profile and goals" },
                  { step: "2", title: "Policy Design", desc: "Select optimal carrier and policy structure" },
                  { step: "3", title: "Financing Arrangement", desc: "Secure competitive financing terms" },
                  { step: "4", title: "Ongoing Management", desc: "Annual reviews and strategy adjustments" }
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-8 h-8 rounded-full bg-[#C5A059]/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-[#C5A059] font-semibold text-sm">{item.step}</span>
                    </div>
                    <div>
                      <h4 className="text-white font-medium mb-1">{item.title}</h4>
                      <p className="text-slate-400 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl text-white mb-4">
              Potential Benefits
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              When structured properly for appropriate candidates, premium financing may offer several advantages.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div
                  key={index}
                  className="bg-slate-900/50 border border-slate-800 p-6 hover:border-[#C5A059]/30 transition-colors"
                >
                  <div className="w-12 h-12 rounded-lg bg-[#C5A059]/10 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-[#C5A059]" />
                  </div>
                  <h3 className="text-white font-semibold mb-2">{benefit.title}</h3>
                  <p className="text-slate-400 text-sm">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h2 className="font-serif text-3xl text-white mb-6">
                Who May Benefit
              </h2>
              <p className="text-slate-400 mb-8">
                Premium financing is typically suitable for individuals and families who meet specific financial criteria and have particular planning needs.
              </p>
              <ul className="space-y-4">
                {suitableFor.map((item, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-[#C5A059] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h2 className="font-serif text-3xl text-white mb-6">
                Important Considerations
              </h2>
              <p className="text-slate-400 mb-8">
                Like any sophisticated financial strategy, premium financing carries risks that must be carefully evaluated.
              </p>
              <div className="space-y-6">
                {considerations.map((item, index) => (
                  <div key={index} className="border-l-2 border-slate-700 pl-4">
                    <h3 className="text-white font-medium mb-1">{item.title}</h3>
                    <p className="text-slate-400 text-sm">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-3xl text-white mb-6">
            Is Premium Financing Right for You?
          </h2>
          <p className="text-slate-400 mb-8 max-w-2xl mx-auto">
            Our specialists can help you evaluate whether premium financing aligns with your financial goals and circumstances. Schedule a confidential consultation.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center bg-[#C5A059] text-[#020617] hover:bg-[#D4AF37] font-semibold px-8 py-4 uppercase tracking-wider text-sm transition-all group"
          >
            Explore Premium Financing
            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PremiumFinancingPage;
